import { useMemo, useEffect } from "react";
import { arrayOf, bool, func, number, shape, string } from "prop-types";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import { useForm, useWatch } from "react-hook-form";
import {
  Button,
  InfoLabel,
  ControlledCheckboxGroup,
  ControlledCheckbox,
  ControlledRadioGroup,
} from "@components";
import { IssueTypeSelect } from "./components/IssueTypeSelect";
import { CircularProgress, Divider, Grid, Tooltip } from "@material-ui/core";
import { SplitLevelsSelect } from "@forms/IssueMappingForm/components/SplitLevelsSelect";

const newProjectFormSchema = (t) =>
  yup
    .object()
    .shape({
      workTypes: yup
        .array()
        .of(yup.string().required(t("errors.project.workType.required")))
        .min(1, t("errors.project.workType.required")),
      mappingSections: yup
        .array()
        .of(yup.string().required(t("errors.bt.mappingSections.required")))
        .min(1, t("errors.bt.mappingSections.required")),
      mergeWorkTypes: yup.number(),
      splitLevels: yup.array().of(yup.string()),
      
      level0Used: yup.bool(),
      level0Map: yup.string().when("level0Used", {
        is: true,
        then: (f) => f.required(t("errors.jira.issueType.required")),
      }),
      level1Used: yup.bool(),
      level1Map: yup.string().when("level1Used", {
        is: true,
        then: (f) => f.required(t("errors.jira.issueType.required")),
      }),
      level2Used: yup.bool(),
      level2Map: yup.string().when("level2Used", {
        is: true,
        then: (f) => f.required(t("errors.jira.issueType.required")),
      }),
      level3Used: yup.bool(),
      level3Map: yup.string().when("level3Used", {
        is: true,
        then: (f) => f.required(t("errors.jira.issueType.required")),
      }),
    })
    .test("at-least-one-level", function (value) {
      return (
        value.level0Used ||
        value.level1Used ||
        value.level2Used ||
        value.level3Used
      );
    });

export const IssueMappingForm = observer(({
  onSubmit,
  issueTypes,
  selectedVersion,
  projectViewLevel = 3
}) => {
  const { t } = useTranslation();
  const defaultValues = useMemo(
    () => ({
      mergeWorkTypes: "1",
      level0Used: true,
      workTypes: selectedVersion?.workTypes?.map((wT) => wT.id) || [],
      splitLevels: [],
      mappingSections: selectedVersion?.sections?.filter((ob) => !ob.turnOff ).map((wT) => wT.id) || [],
    }),
    []
  );

  const {
    control,
    handleSubmit,
    setError,
    setValue,
    clearErrors,
    reset: resetForm,
    formState: { isSubmitting, errors },
  } = useForm({
    resolver: yupResolver(newProjectFormSchema(t)),
    defaultValues,
  });
  
  const workTypes = useWatch({
    control, exact: true, name: "workTypes",
  });
  
  const splitWorkTypes = useWatch({
    control, exact: true, name: "mergeWorkTypes",
    compute: (data) => data === "0",
  });

  const levelsUsed = useWatch({
    control, exact: true,
    name: ["level0Used", "level1Used", "level2Used", "level3Used"],
  });
  
  const levelMaps = useWatch({
    control, exact: true,
    name: ["level0Map", "level1Map", "level2Map", "level3Map"],
  });
  
  const hasUsedLevels = useMemo(() => levelsUsed.includes(true), [levelsUsed]);
  const usedMaps = useMemo(() => levelMaps.filter(m => m), [levelMaps]);
  
  const submitHandler = async (data) => {
    const result = await onSubmit(data);

    if (result) {
      const { errors, reset } = result;

      if (reset) {
        resetForm(defaultValues);
        return;
      }

      errors?.forEach(({ field, message }) => {
        setError(field, { message });
      });
    }
  };

  useEffect(() => {
    if (!hasUsedLevels && !errors.checkboxGroup) {
      setError("checkboxGroup", {
        type: "custom",
        message: t("errors.bt.issueType.required"),
      });
    } else if(errors.checkboxGroup) {
      clearErrors("checkboxGroup");
    }
  }, [hasUsedLevels]);

  useEffect(() => {
    setValue("splitLevels", usedMaps);
  }, [usedMaps]);

  return (
    <form
      autoComplete="on"
      aria-label="companyDetails form"
      onSubmit={handleSubmit(submitHandler)}
    >
      <Grid item container direction="column" wrap="nowrap" className="p-6">
        <InfoLabel spacing={8} label="views.bt.select_sections" />
        {
          Boolean(selectedVersion.sections) &&
          <ControlledCheckboxGroup
            control={control}
            name="mappingSections"
            options={selectedVersion?.sections?.filter((ob) => !ob.turnOff )}
          />
        }
        <InfoLabel spacing={8} label="views.bt.levels_of_estimate" />
        {[0, 1, 2, 3].map((level) => (
          <Grid
            key={level}
            item
            container
            justifyContent="space-between"
            alignItems="center"
            className="mb-2 h-36"
          >
            <Tooltip
              title={t("views.bt.no_view_level", {
                levelName: t(
                  `proposal_parts.modules_levels.${level}`
                ).toLowerCase(),
              })}
              disableHoverListener={level <= projectViewLevel}
            >
              <Grid item>
                <ControlledCheckbox
                  name={`level${level}Used`}
                  control={control}
                  label={t(`proposal_parts.modules_levels.${level}`)}
                  disabled={level > projectViewLevel}
                />
              </Grid>
            </Tooltip>

            {
              levelsUsed[level] &&
              <Grid item>
                <IssueTypeSelect
                  control={control}
                  level={level}
                  issueTypes={issueTypes}
                  parentIssueLevels={levelMaps.filter((_, i) => i < level)}
                />
              </Grid>
            }
          </Grid>
        ))}
        {
          errors.checkboxGroup &&
          <span className="mt-2 color-error">
            {errors.checkboxGroup.message}
          </span>
        }{" "}
        <InfoLabel spacing={8} label="views.bt.work_type" />
        {
          Boolean(selectedVersion?.workTypes) &&
          <ControlledCheckboxGroup
            control={control}
            name="workTypes"
            options={selectedVersion.workTypes}
          />
        }
        <InfoLabel spacing={8} label="views.bt.multiple_work_types" />
        <ControlledRadioGroup
          control={control}
          name="mergeWorkTypes"
          options={[
            { value: 1, label: t("views.bt.single_ticket_work_type") },
            { value: 0, label: t("views.bt.split_ticket_work_type") },
          ]}
        />
        {
          splitWorkTypes &&
          <>
            <InfoLabel spacing={8} label="views.bt.split_levels" />
            <SplitLevelsSelect
              control={control}
              name="splitLevels"
              workTypes={selectedVersion.workTypes}
              usedWorkTypes={workTypes}
              issueTypes={issueTypes}
              usedMaps={usedMaps}
            />
          </>
        }
        <Divider className="mt-2 -mx-6 mb-6" />
        <Grid item container justifyContent="flex-end">
          <Button
            isSubmit
            name="submit"
            variant="contained"
            endIcon={
              isSubmitting ? (
                <CircularProgress
                  aria-label="progress indicator"
                  size={24}
                  color="inherit"
                />
              ) : undefined
            }
          >
            { t("common.export") }
          </Button>
        </Grid>
      </Grid>
    </form>
  );
});

IssueMappingForm.propTypes = {
  onSubmit: func,
  projectViewLevel: number,
  selectedVersion: Object,
  issueTypes: arrayOf(
    shape({
      id: string.isRequired,
      name: string.isRequired,
      lvl: number.isRequired,
      subtask: bool,
    })
  ).isRequired,
};
